import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell,
  type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type Priority = 'Low' | 'Medium' | 'High' | 'Critical';
type AssetCriticality = 'Low' | 'Medium' | 'High' | 'Critical';
type RecommendationStatus = 'pending' | 'approved' | 'dismissed';
type AssetType = 'application' | 'device' | 'server' | 'identity' | 'data_source' | 'business_process' | 'network' | 'ip' | 'domain' | 'url' | 'cloud_app' | 'cve' | 'file_hash' | 'process' | 'mailbox' | 'other';

interface QueryTable {
  id: string;
  title: string;
  columns: string[];
  rows: Record<string, unknown>[];
  warnings: string[];
  skipped?: boolean;
}

interface CriticalAsset {
  id: string;
  assetType: AssetType;
  assetValue: string;
  displayName: string;
  businessUnit: string | null;
  criticality: AssetCriticality;
  supportedApplications: string[];
  owner: string | null;
  tags: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AssetRecommendation {
  id: string;
  customerId: string;
  assetType: AssetType;
  assetValue: string;
  displayName: string;
  inferredCriticality: AssetCriticality;
  confidence: number;
  recommendationScore: number;
  status: RecommendationStatus;
  reason: string;
  evidence: string[];
  sourceSignals: string[];
  suggestedBusinessUnit: string | null;
  suggestedOwner: string | null;
  suggestedTags: string[];
  suggestedSupportedApplications: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  approvedAssetId: string | null;
  dismissedReason: string | null;
}

interface AssetRecommendationsPayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  recommendations: AssetRecommendation[];
  counters: { pending: number; approved: number; dismissed: number; scanned: number; cached: number };
  scanTables: QueryTable[];
  diagnostics: string[];
}

interface DecisionRow {
  id: string;
  targetType: 'incident' | 'vulnerability' | 'asset';
  target: string;
  currentPriority: Priority;
  recommendedPriority: Priority;
  confidence: number;
  reason: string;
  affectedCriticalAssets: string[];
  evidence: string[];
  recommendedActions: string[];
}

interface TwinNode {
  id: string;
  type: string;
  label: string;
  risk: number;
  count: number;
  businessCritical?: boolean;
  details?: string;
}

interface TwinEdge {
  from: string;
  to: string;
  label: string;
  weight: number;
}

interface DigitalTwinPayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  score: {
    decisionScore: number;
    riskLevel: string;
    modelCompleteness: number;
    summary: string;
  };
  counters: Record<string, number>;
  model: {
    customers: Array<Record<string, unknown>>;
    users: Record<string, unknown>[];
    devices: Record<string, unknown>[];
    identities: Record<string, unknown>[];
    vulnerabilities: Record<string, unknown>[];
    detections: Record<string, unknown>[];
    incidents: Record<string, unknown>[];
    controls: Record<string, unknown>[];
    playbooks: Record<string, unknown>[];
    dataSources: Record<string, unknown>[];
    businessCriticalAssets: CriticalAsset[];
    businessCriticalAssetRecommendations?: AssetRecommendation[];
  };
  graph: {
    nodes: TwinNode[];
    edges: TwinEdge[];
  };
  decisionEngine: DecisionRow[];
  recommendedActions: Array<{ priority: Priority; title: string; details: string; owner: string }>;
  tables: QueryTable[];
  diagnostics: string[];
}

interface AssetFormState {
  id?: string;
  assetType: AssetType;
  assetValue: string;
  displayName: string;
  businessUnit: string;
  criticality: AssetCriticality;
  supportedApplications: string;
  owner: string;
  tags: string;
  notes: string;
}

const LOOKBACKS: Lookback[] = ['24h', '7d', '30d', '60d', '90d'];
const ASSET_TYPES: AssetType[] = ['application', 'cloud_app', 'device', 'server', 'identity', 'ip', 'domain', 'url', 'cve', 'data_source', 'business_process', 'network', 'file_hash', 'process', 'mailbox', 'other'];

const emptyAssetForm: AssetFormState = {
  assetType: 'device',
  assetValue: '',
  displayName: '',
  businessUnit: '',
  criticality: 'High',
  supportedApplications: '',
  owner: '',
  tags: '',
  notes: '',
};

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(cell).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function pillTone(value: string): string {
  const v = value.toLowerCase();
  if (v.includes('critical')) return 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200';
  if (v.includes('high')) return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200';
  if (v.includes('medium')) return 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-500/40 dark:bg-yellow-500/10 dark:text-yellow-100';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-200';
}

function priorityRank(priority: Priority): number {
  return { Low: 1, Medium: 2, High: 3, Critical: 4 }[priority] ?? 1;
}

function csv(value: string): string[] {
  return value.split(',').map((v) => v.trim()).filter(Boolean);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : fmtTs(value);
}

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

async function exportDigitalTwinPdf(payload: DigitalTwinPayload, decisions: DecisionRow[], filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'SOC Digital Twin',
    customerName: payload.customerName,
    periodLabel: `${payload.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Digital Twin Overview', 'primary');
  const tiles: KpiTile[] = [
    { value: `${payload.score.decisionScore}/100`, label: 'Digital Twin Risk', sublabel: payload.score.riskLevel, tone: payload.score.decisionScore >= 70 ? 'danger' : payload.score.decisionScore >= 40 ? 'warning' : 'success' },
    { value: `${payload.score.modelCompleteness}%`, label: 'Model Completeness', tone: 'info' },
    { value: String(decisions.filter((row) => row.recommendedPriority === 'Critical' && row.currentPriority !== 'Critical').length), label: 'Critical Upgrades', sublabel: 'Business-aware escalations', tone: 'danger' },
    { value: String(payload.counters.businessCriticalAssets ?? 0), label: 'Business-Critical Assets', sublabel: `${payload.counters.incidents ?? 0} incidents · ${payload.counters.vulnerabilities ?? 0} vulnerabilities`, tone: 'default' },
  ];
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: payload.score.decisionScore >= 70 ? 'critical' : payload.score.decisionScore >= 40 ? 'warning' : 'success',
    title: `Decision Score: ${payload.score.decisionScore}/100 (${payload.score.riskLevel})`,
    body: payload.score.summary,
  });

  drawSectionBanner(ctx, 'Business-Aware Priority Decisions', 'orange');
  if (decisions.length) {
    const decisionRows: TableCell[][] = decisions.slice(0, 20).map((row) => [
      text(row.targetType, 18),
      text(row.target, 30),
      severityCell(row.currentPriority),
      severityCell(row.recommendedPriority),
      `${row.confidence}%`,
      text(row.reason, 130),
    ]);
    drawTable(ctx, {
      head: ['Type', 'Target', 'Current', 'Recommended', 'Confidence', 'Reason'],
      body: decisionRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 30 },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 'auto' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No business-aware priority decisions for the selected lookback.');
  }

  drawSectionBanner(ctx, 'Business-Critical Assets', 'primary');
  const assets = payload.model.businessCriticalAssets ?? [];
  if (assets.length) {
    const assetRows: TableCell[][] = assets.slice(0, 25).map((asset) => [
      text(asset.assetType, 18),
      text(asset.displayName, 40),
      text(asset.assetValue, 30),
      severityCell(asset.criticality),
      text(asset.businessUnit ?? '-', 24),
      text(asset.owner ?? '-', 22),
    ]);
    drawTable(ctx, {
      head: ['Type', 'Name', 'Identifier', 'Criticality', 'Business Unit', 'Owner'],
      body: assetRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 30 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 26 },
        5: { cellWidth: 24 },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No business-critical assets registered for this customer.');
  }

  drawSectionBanner(ctx, 'Recommended Actions', 'primary');
  if (payload.recommendedActions.length) {
    const recRows: TableCell[][] = payload.recommendedActions.slice(0, 16).map((rec) => [
      severityCell(rec.priority),
      text(rec.title, 40),
      text(rec.details, 140),
      text(rec.owner, 24),
    ]);
    drawTable(ctx, {
      head: ['Priority', 'Title', 'Details', 'Owner'],
      body: recRows,
      columnStyles: {
        0: { cellWidth: 20, halign: 'center' },
        1: { cellWidth: 38 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 24 },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No recommended actions for the selected lookback.');
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

function apiErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function useDynamicColumns(table: QueryTable | undefined, storageKey: string): FilterableColumnDef<Record<string, unknown>>[] {
  return useMemo(() => {
    const columns = table?.columns?.length ? table.columns : Object.keys(table?.rows?.[0] ?? {});
    return columns.slice(0, 12).map((column) => ({
      key: `${storageKey}-${column}`,
      header: column,
      headerText: column,
      value: (row: Record<string, unknown>) => cell(row[column]),
      render: (row: Record<string, unknown>) => <span className="break-words text-xs text-muted-foreground">{cell(row[column]) || '-'}</span>,
    }));
  }, [storageKey, table]);
}

function MetricCard({ label, value, subtext }: { label: string; value: string | number; subtext?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card/80 p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-foreground">{value}</div>
      {subtext ? <div className="mt-1 text-xs text-muted-foreground">{subtext}</div> : null}
    </div>
  );
}

function DataTable({ table, storageKey }: { table: QueryTable; storageKey: string }) {
  const columns = useDynamicColumns(table, storageKey);
  return (
    <div className="rounded-2xl border border-border bg-card/70 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{table.title}</h3>
          <p className="text-xs text-muted-foreground">{table.rows.length} row(s){table.skipped ? ' · unavailable/skipped' : ''}</p>
        </div>
        {table.warnings.length > 0 ? <span className="rounded-full border border-yellow-500/40 px-2 py-1 text-xs text-yellow-100">{table.warnings.length} warning(s)</span> : null}
      </div>
      {table.warnings.length > 0 ? (
        <div className="mb-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-100">
          {table.warnings.slice(0, 3).join(' | ')}
        </div>
      ) : null}
      <FilterableColumnTable
        columns={columns}
        rows={table.rows}
        storageKey={`soc-digital-twin-${storageKey}`}
        rowKey={(_, index) => `${storageKey}-${index}`}
        emptyMessage="No data available for this layer."
        maxHeightClassName="max-h-80"
        canFilterColumns
        canResizeColumns
      />
    </div>
  );
}

function rowsToTable(id: string, title: string, rows: Record<string, unknown>[], warnings: string[] = []): QueryTable {
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).slice(0, 16);
  return { id, title, columns, rows, warnings };
}

function tableById(payload: DigitalTwinPayload, id: string, title: string, fallbackRows: Record<string, unknown>[]): QueryTable {
  return payload.tables.find((table) => table.id === id) ?? rowsToTable(id, title, fallbackRows);
}

export function SocDigitalTwinTab({ customerId, customerName }: { customerId?: string; customerName?: string }) {
  const { canCapability } = useAuth();
  const [lookback, setLookback] = useState<Lookback>('30d');
  const [payload, setPayload] = useState<DigitalTwinPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [assetBusy, setAssetBusy] = useState(false);
  const [recommendationBusy, setRecommendationBusy] = useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);
  const [recommendationPayload, setRecommendationPayload] = useState<AssetRecommendationsPayload | null>(null);
  const [assetForm, setAssetForm] = useState<AssetFormState>(emptyAssetForm);

  const canRead = canCapability('soc_digital_twin', 'read');
  const canRefresh = canCapability('soc_digital_twin', 'refresh');
  const canManageAssets = canCapability('soc_digital_twin', 'manage_business_assets');
  const canRecommendAssets = canCapability('soc_digital_twin', 'recommend_assets');
  const canApproveRecommendations = canCapability('soc_digital_twin', 'approve_asset_recommendations');
  const canAnalyzeIncident = canCapability('soc_digital_twin', 'analyze_incident');
  const canAnalyzeVulnerability = canCapability('soc_digital_twin', 'analyze_vulnerability');
  const canExport = canCapability('soc_digital_twin', 'export_report');

  const load = useCallback(async (refresh = false) => {
    if (!customerId || !canRead) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<DigitalTwinPayload>(`/soc-digital-twin/summary?customerId=${encodeURIComponent(customerId)}&lookback=${lookback}${refresh ? '&refresh=1' : ''}`);
      setPayload(data);
      if (refresh) toast({ title: 'SOC Digital Twin refreshed', description: 'The customer security model has been rebuilt and audited.' });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [canRead, customerId, lookback]);

  useEffect(() => {
    void load(false);
  }, [load]);

  const decisionColumns = useMemo<FilterableColumnDef<DecisionRow>[]>(() => [
    { key: 'targetType', header: 'Type', value: (row) => row.targetType, render: (row) => <span className="capitalize">{row.targetType}</span> },
    { key: 'target', header: 'Target', value: (row) => row.target, render: (row) => <span className="font-medium text-foreground">{row.target}</span> },
    { key: 'currentPriority', header: 'Current', value: (row) => row.currentPriority, render: (row) => <span className={`rounded-full border px-2 py-1 text-xs ${pillTone(row.currentPriority)}`}>{row.currentPriority}</span> },
    { key: 'recommendedPriority', header: 'Recommended', value: (row) => row.recommendedPriority, render: (row) => <span className={`rounded-full border px-2 py-1 text-xs ${pillTone(row.recommendedPriority)}`}>{row.recommendedPriority}</span> },
    { key: 'confidence', header: 'Confidence', value: (row) => row.confidence, render: (row) => `${row.confidence}%` },
    { key: 'reason', header: 'Reason', value: (row) => row.reason, render: (row) => <span className="text-xs text-muted-foreground">{row.reason}</span> },
    { key: 'affectedCriticalAssets', header: 'Critical Assets', value: (row) => row.affectedCriticalAssets.join(', '), render: (row) => <span className="text-xs">{row.affectedCriticalAssets.join(', ') || '-'}</span> },
    { key: 'recommendedActions', header: 'Actions', value: (row) => row.recommendedActions.join(', '), render: (row) => <span className="text-xs text-muted-foreground">{row.recommendedActions.join(' | ')}</span> },
  ], []);

  const assetColumns = useMemo<FilterableColumnDef<CriticalAsset>[]>(() => [
    { key: 'assetType', header: 'Type', value: (row) => row.assetType },
    { key: 'displayName', header: 'Name', value: (row) => row.displayName, render: (row) => <span className="font-medium text-foreground">{row.displayName}</span> },
    { key: 'assetValue', header: 'Identifier', value: (row) => row.assetValue },
    { key: 'criticality', header: 'Criticality', value: (row) => row.criticality, render: (row) => <span className={`rounded-full border px-2 py-1 text-xs ${pillTone(row.criticality)}`}>{row.criticality}</span> },
    { key: 'businessUnit', header: 'Business Unit', value: (row) => row.businessUnit ?? '' },
    { key: 'supportedApplications', header: 'Applications', value: (row) => row.supportedApplications.join(', ') },
    { key: 'owner', header: 'Owner', value: (row) => row.owner ?? '' },
    { key: 'actions', header: 'Actions', filterEnabled: false, resizeEnabled: false, render: (row) => canManageAssets ? (
      <div className="flex gap-2">
        <button type="button" className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => setAssetForm({
          id: row.id,
          assetType: row.assetType,
          assetValue: row.assetValue,
          displayName: row.displayName,
          businessUnit: row.businessUnit ?? '',
          criticality: row.criticality,
          supportedApplications: row.supportedApplications.join(', '),
          owner: row.owner ?? '',
          tags: row.tags.join(', '),
          notes: row.notes ?? '',
        })}>Edit</button>
        <button type="button" className="rounded-lg border border-red-500/40 px-2 py-1 text-xs text-red-200 hover:bg-red-500/10" onClick={() => void deleteAsset(row.id)}>Delete</button>
      </div>
    ) : <span className="text-xs text-muted-foreground">Read only</span> },
  ], [canManageAssets, customerId]);

  const recommendationColumns = useMemo<FilterableColumnDef<AssetRecommendation>[]>(() => [
    { key: 'assetType', header: 'Type', value: (row) => row.assetType, render: (row) => <span className="capitalize">{row.assetType.replace(/_/g, ' ')}</span> },
    { key: 'displayName', header: 'Recommended Asset', value: (row) => row.displayName, render: (row) => <span className="font-medium text-foreground">{row.displayName}</span> },
    { key: 'assetValue', header: 'Identifier', value: (row) => row.assetValue, render: (row) => <span className="font-mono text-xs">{row.assetValue}</span> },
    { key: 'inferredCriticality', header: 'Criticality', value: (row) => row.inferredCriticality, render: (row) => <span className={`rounded-full border px-2 py-1 text-xs ${pillTone(row.inferredCriticality)}`}>{row.inferredCriticality}</span> },
    { key: 'recommendationScore', header: 'Score', value: (row) => row.recommendationScore, render: (row) => <span className="font-semibold">{row.recommendationScore}</span> },
    { key: 'confidence', header: 'Confidence', value: (row) => row.confidence, render: (row) => `${row.confidence}%` },
    { key: 'reason', header: 'Reason', value: (row) => row.reason, render: (row) => <span className="block max-w-[520px] text-xs text-muted-foreground">{row.reason}</span> },
    { key: 'evidence', header: 'Evidence', value: (row) => row.evidence.join(' | '), render: (row) => <span className="block max-w-[520px] text-xs text-muted-foreground">{row.evidence.slice(0, 3).join(' | ') || '-'}</span> },
    { key: 'sourceSignals', header: 'Sources', value: (row) => row.sourceSignals.join(', '), render: (row) => <span className="text-xs text-muted-foreground">{row.sourceSignals.slice(0, 4).join(', ') || '-'}</span> },
    { key: 'lastSeenAt', header: 'Last Seen', value: (row) => row.lastSeenAt, render: (row) => <span className="text-xs text-muted-foreground">{formatDate(row.lastSeenAt)}</span> },
    { key: 'actions', header: 'Actions', filterEnabled: false, resizeEnabled: false, render: (row) => canApproveRecommendations ? (
      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-lg bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50" disabled={recommendationBusy} onClick={() => void approveRecommendation(row.id)}>Approve</button>
        <button type="button" className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50" disabled={recommendationBusy} onClick={() => setAssetForm({
          assetType: row.assetType,
          assetValue: row.assetValue,
          displayName: row.displayName,
          businessUnit: row.suggestedBusinessUnit ?? '',
          criticality: row.inferredCriticality,
          supportedApplications: row.suggestedSupportedApplications.join(', '),
          owner: row.suggestedOwner ?? '',
          tags: row.suggestedTags.join(', '),
          notes: `Recommended by SOC Digital Twin. ${row.reason}`,
        })}>Edit first</button>
        <button type="button" className="rounded-lg border border-amber-500/40 px-2 py-1 text-xs text-amber-200 hover:bg-amber-500/10 disabled:opacity-50" disabled={recommendationBusy} onClick={() => void dismissRecommendation(row.id)}>Dismiss</button>
      </div>
    ) : <span className="text-xs text-muted-foreground">Approval restricted</span> },
  ], [canApproveRecommendations, recommendationBusy]);

  const recommendations = useMemo(() => (recommendationPayload?.recommendations ?? payload?.model.businessCriticalAssetRecommendations ?? [])
    .slice()
    .sort((a, b) => b.recommendationScore - a.recommendationScore || b.confidence - a.confidence), [payload?.model.businessCriticalAssetRecommendations, recommendationPayload?.recommendations]);

  const decisions = useMemo(() => (payload?.decisionEngine ?? [])
    .slice()
    .sort((a, b) => priorityRank(b.recommendedPriority) - priorityRank(a.recommendedPriority) || b.confidence - a.confidence), [payload]);

  async function loadRecommendations(refresh = false): Promise<void> {
    if (!customerId || !canRecommendAssets) return;
    setRecommendationBusy(true);
    setRecommendationError(null);
    try {
      const data = await apiFetch<AssetRecommendationsPayload>(`/soc-digital-twin/asset-recommendations?customerId=${encodeURIComponent(customerId)}&lookback=${lookback}${refresh ? '&refresh=1' : ''}`);
      setRecommendationPayload(data);
      toast({ title: refresh ? 'Critical asset scan completed' : 'Critical asset recommendations loaded', description: `${data.recommendations.length} pending recommendation(s) available.` });
    } catch (e) {
      const message = apiErrorMessage(e);
      setRecommendationError(message);
      toast({ title: 'Critical asset recommendation scan failed', description: message });
    } finally {
      setRecommendationBusy(false);
    }
  }

  async function approveRecommendation(id: string): Promise<void> {
    if (!customerId || !canApproveRecommendations) return;
    setRecommendationBusy(true);
    try {
      await apiFetch<{ ok: true; asset: CriticalAsset; recommendation: AssetRecommendation }>(`/soc-digital-twin/asset-recommendations/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      toast({ title: 'Recommended asset approved', description: 'The entity was added to Business-Critical Asset Map and the action was audited.' });
      await Promise.all([load(true), loadRecommendations(false)]);
    } catch (e) {
      toast({ title: 'Failed to approve recommendation', description: apiErrorMessage(e) });
    } finally {
      setRecommendationBusy(false);
    }
  }

  async function dismissRecommendation(id: string): Promise<void> {
    if (!customerId || !canApproveRecommendations) return;
    setRecommendationBusy(true);
    try {
      await apiFetch<{ ok: true; dismissed: number }>(`/soc-digital-twin/asset-recommendations/${encodeURIComponent(id)}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, reason: 'Dismissed from SOC Digital Twin recommendation queue.' }),
      });
      toast({ title: 'Recommendation dismissed', description: 'The decision was audited and removed from the pending queue.' });
      await loadRecommendations(false);
    } catch (e) {
      toast({ title: 'Failed to dismiss recommendation', description: apiErrorMessage(e) });
    } finally {
      setRecommendationBusy(false);
    }
  }

  async function saveAsset(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!customerId || !canManageAssets) return;
    if (!assetForm.assetValue.trim()) {
      toast({ title: 'Asset identifier required', description: 'Enter a hostname, UPN, application name, data source or business process name.' });
      return;
    }
    setAssetBusy(true);
    try {
      await apiFetch<{ ok: true; asset: CriticalAsset }>('/soc-digital-twin/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          id: assetForm.id,
          assetType: assetForm.assetType,
          assetValue: assetForm.assetValue,
          displayName: assetForm.displayName,
          businessUnit: assetForm.businessUnit,
          criticality: assetForm.criticality,
          supportedApplications: csv(assetForm.supportedApplications),
          owner: assetForm.owner,
          tags: csv(assetForm.tags),
          notes: assetForm.notes,
        }),
      });
      setAssetForm(emptyAssetForm);
      toast({ title: 'Business asset saved', description: 'The Digital Twin asset map was updated and audited.' });
      await load(true);
      if (canRecommendAssets) await loadRecommendations(false);
    } catch (e) {
      toast({ title: 'Failed to save asset', description: apiErrorMessage(e) });
    } finally {
      setAssetBusy(false);
    }
  }

  async function deleteAsset(id: string): Promise<void> {
    if (!customerId || !canManageAssets) return;
    setAssetBusy(true);
    try {
      await fetch(`${getApiBase()}/soc-digital-twin/assets/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, {
        method: 'DELETE',
        credentials: 'include',
      }).then(async (response) => {
        if (!response.ok) throw new Error((await response.json().catch(() => ({})) as { message?: string }).message ?? `Delete failed: ${response.status}`);
      });
      toast({ title: 'Business asset deleted', description: 'The Digital Twin asset map was updated and audited.' });
      await load(true);
      if (canRecommendAssets) await loadRecommendations(false);
    } catch (e) {
      toast({ title: 'Failed to delete asset', description: apiErrorMessage(e) });
    } finally {
      setAssetBusy(false);
    }
  }

  async function exportReport(): Promise<void> {
    if (!customerId || !canExport || !payload) return;
    setReportBusy(true);
    try {
      await exportDigitalTwinPdf(payload, decisions, `soc-digital-twin-${customerId}`);
      toast({ title: 'SOC Digital Twin report exported', description: 'The export action was audited.' });
    } catch (e) {
      toast({ title: 'Report export failed', description: apiErrorMessage(e) });
    } finally {
      setReportBusy(false);
    }
  }

  if (!customerId) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Select a customer to build the SOC Digital Twin. The default MSSP workspace is intentionally not used for tenant security modeling.
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        You do not have SOC Digital Twin read permission. Enable it in Masters → Permissions / Capability Matrix.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/20 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Security decision engine</div>
            <h2 className="mt-1 text-2xl font-semibold text-foreground">SOC Digital Twin</h2>
            <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
              Models the customer, identities, devices, vulnerabilities, detections, incidents, controls, playbooks, data sources and business-critical assets, then recommends business-aware incident and vulnerability priority changes.
            </p>
            <div className="mt-3 text-xs text-muted-foreground">
              Customer: <span className="font-medium text-foreground">{customerName ?? payload?.customerName ?? customerId}</span>
              {payload ? <> · Generated: {formatDate(payload.generatedAt)} · {payload.cacheHit ? 'DB cache' : 'fresh model'}</> : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded-lg border border-border bg-background px-3 py-2 text-sm" value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)}>
              {LOOKBACKS.map((lb) => <option key={lb} value={lb}>{lb}</option>)}
            </select>
            <button type="button" className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted" onClick={() => void load(false)} disabled={loading}>Load</button>
            <button type="button" className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50" onClick={() => void load(true)} disabled={loading || !canRefresh}>{loading ? 'Refreshing…' : 'Refresh'}</button>
            <button type="button" className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50" onClick={() => void exportReport()} disabled={reportBusy || !canExport || !payload}>{reportBusy ? 'Exporting…' : 'Export Report'}</button>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100">{error}</div> : null}

      {payload ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Digital Twin Risk" value={`${payload.score.decisionScore}/100`} subtext={payload.score.riskLevel} />
            <MetricCard label="Model Completeness" value={`${payload.score.modelCompleteness}%`} subtext="Coverage across model layers" />
            <MetricCard label="Critical Upgrades" value={decisions.filter((row) => row.recommendedPriority === 'Critical' && row.currentPriority !== 'Critical').length} subtext="Business-aware recommended escalations" />
            <MetricCard label="Business-Critical Assets" value={payload.counters.businessCriticalAssets ?? 0} subtext={`${payload.counters.incidents ?? 0} incidents · ${payload.counters.vulnerabilities ?? 0} vulnerabilities`} />
          </div>

          {payload.diagnostics.length > 0 ? (
            <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm text-yellow-100">
              <div className="font-medium">Model warnings</div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {payload.diagnostics.slice(0, 8).map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
              </ul>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-card/70 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Decision Engine</h3>
                <p className="text-sm text-muted-foreground">Recommended priority changes based on business-criticality, vulnerability exposure, previous incident history, detections and available controls.</p>
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground">
                {canAnalyzeIncident ? <span className="rounded-full border border-border px-2 py-1">Incident analysis enabled</span> : null}
                {canAnalyzeVulnerability ? <span className="rounded-full border border-border px-2 py-1">Vulnerability analysis enabled</span> : null}
              </div>
            </div>
            <FilterableColumnTable
              columns={decisionColumns}
              rows={decisions}
              storageKey="soc-digital-twin-decisions"
              rowKey={(row) => row.id}
              emptyMessage="No priority upgrade recommendations were generated for this model."
              maxHeightClassName="max-h-96"
              canFilterColumns
              canResizeColumns
              rowClassName={(row) => priorityRank(row.recommendedPriority) > priorityRank(row.currentPriority) ? 'bg-red-500/5' : ''}
            />
            {payload.recommendedActions.length > 0 ? (
              <div className="mt-4 rounded-xl border border-border bg-background/50 p-4">
                <div className="text-sm font-semibold text-foreground">Recommended SOC actions</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {payload.recommendedActions.map((action, index) => <li key={`${action.title}-${index}`}><span className={`rounded-full border px-2 py-0.5 text-xs ${pillTone(action.priority)}`}>{action.priority}</span> <span className="font-medium text-foreground">{action.title}</span> - {action.details} <span className="text-xs">Owner: {action.owner}</span></li>)}
                </ul>
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-2xl border border-border bg-card/70 p-4">
              <h3 className="text-lg font-semibold text-foreground">Twin Graph</h3>
              <p className="text-sm text-muted-foreground">Compact relationship map across customer, incidents, detections, vulnerabilities, assets and controls.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {payload.graph.nodes.map((node) => (
                  <div key={node.id} className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs uppercase text-muted-foreground">{node.type}</span>
                      <span className={`rounded-full border px-2 py-1 text-xs ${node.risk >= 75 ? pillTone('Critical') : node.risk >= 55 ? pillTone('High') : pillTone('Medium')}`}>Risk {node.risk}</span>
                    </div>
                    <div className="mt-2 font-medium text-foreground">{node.label}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Count: {node.count}{node.businessCritical ? ' · business critical' : ''}</div>
                    {node.details ? <div className="mt-2 text-xs text-muted-foreground">{node.details}</div> : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card/70 p-4">
              <h3 className="text-lg font-semibold text-foreground">Relationships</h3>
              <div className="mt-4 space-y-2">
                {payload.graph.edges.map((edge, index) => (
                  <div key={`${edge.from}-${edge.to}-${index}`} className="rounded-xl border border-border bg-background/50 p-3 text-sm">
                    <div className="text-foreground">{edge.from} → {edge.to}</div>
                    <div className="text-xs text-muted-foreground">{edge.label} · weight {edge.weight}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card/70 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Business-Critical Asset Map</h3>
                <p className="text-sm text-muted-foreground">Use this to teach the Digital Twin which devices, identities, apps, data sources or processes deserve business-aware escalation.</p>
              </div>
            </div>
            {canManageAssets ? (
              <form className="mb-4 grid gap-3 rounded-xl border border-border bg-background/50 p-4 md:grid-cols-2 xl:grid-cols-4" onSubmit={(event) => void saveAsset(event)}>
                <select className="rounded-lg border border-border bg-background px-3 py-2 text-sm" value={assetForm.assetType} onChange={(e) => setAssetForm((prev) => ({ ...prev, assetType: e.target.value as AssetType }))}>
                  {ASSET_TYPES.map((type) => <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>)}
                </select>
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Asset identifier / hostname / UPN" value={assetForm.assetValue} onChange={(e) => setAssetForm((prev) => ({ ...prev, assetValue: e.target.value }))} />
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Display name" value={assetForm.displayName} onChange={(e) => setAssetForm((prev) => ({ ...prev, displayName: e.target.value }))} />
                <select className="rounded-lg border border-border bg-background px-3 py-2 text-sm" value={assetForm.criticality} onChange={(e) => setAssetForm((prev) => ({ ...prev, criticality: e.target.value as AssetCriticality }))}>
                  {(['Critical', 'High', 'Medium', 'Low'] as AssetCriticality[]).map((crit) => <option key={crit} value={crit}>{crit}</option>)}
                </select>
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Business unit" value={assetForm.businessUnit} onChange={(e) => setAssetForm((prev) => ({ ...prev, businessUnit: e.target.value }))} />
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Supported apps, comma separated" value={assetForm.supportedApplications} onChange={(e) => setAssetForm((prev) => ({ ...prev, supportedApplications: e.target.value }))} />
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Owner" value={assetForm.owner} onChange={(e) => setAssetForm((prev) => ({ ...prev, owner: e.target.value }))} />
                <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Tags, comma separated" value={assetForm.tags} onChange={(e) => setAssetForm((prev) => ({ ...prev, tags: e.target.value }))} />
                <textarea className="rounded-lg border border-border bg-background px-3 py-2 text-sm xl:col-span-3" placeholder="Notes" value={assetForm.notes} onChange={(e) => setAssetForm((prev) => ({ ...prev, notes: e.target.value }))} />
                <div className="flex gap-2">
                  <button type="submit" className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50" disabled={assetBusy}>{assetForm.id ? 'Update Asset' : 'Add Asset'}</button>
                  {assetForm.id ? <button type="button" className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted" onClick={() => setAssetForm(emptyAssetForm)}>Cancel</button> : null}
                </div>
              </form>
            ) : null}
            <FilterableColumnTable
              columns={assetColumns}
              rows={payload.model.businessCriticalAssets}
              storageKey="soc-digital-twin-assets"
              rowKey={(row) => row.id}
              emptyMessage="No business-critical assets have been mapped yet."
              maxHeightClassName="max-h-96"
              canFilterColumns
              canResizeColumns
            />

            <div className="mt-5 rounded-2xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Recommended Business-Critical Assets</h3>
                  <p className="text-sm text-muted-foreground">Scan the customer environment for high-value identities, devices, servers, cloud apps, IPs, URLs, data sources and CVEs. Recommendations are persisted in DB, cached, and require analyst approval before monitoring.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canRecommendAssets ? <button type="button" onClick={() => void loadRecommendations(false)} disabled={recommendationBusy} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Load Cached</button> : null}
                  {canRecommendAssets && canRefresh ? <button type="button" onClick={() => void loadRecommendations(true)} disabled={recommendationBusy} className="rounded-xl bg-primary px-3 py-2 text-sm font-bold text-primary-foreground shadow hover:opacity-90 disabled:opacity-60">{recommendationBusy ? 'Scanning…' : 'Scan Environment'}</button> : null}
                </div>
              </div>
              {recommendationError ? <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">{recommendationError}</div> : null}
              {recommendationPayload ? (
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <MetricCard label="Pending" value={recommendationPayload.counters.pending} subtext="awaiting approval" />
                  <MetricCard label="Approved" value={recommendationPayload.counters.approved} subtext="promoted to asset map" />
                  <MetricCard label="Dismissed" value={recommendationPayload.counters.dismissed} subtext="not monitored" />
                  <MetricCard label="Scanned" value={recommendationPayload.counters.scanned} subtext={recommendationPayload.cacheHit ? 'from cache' : 'latest run'} />
                </div>
              ) : null}
              <div className="mt-4">
                <FilterableColumnTable
                  columns={recommendationColumns}
                  rows={recommendations}
                  storageKey="soc-digital-twin-asset-recommendations"
                  rowKey={(row) => row.id}
                  emptyMessage="No pending asset recommendations. Click Scan Environment to build a fresh recommendation queue."
                  maxHeightClassName="max-h-[520px]"
                  canFilterColumns
                  canResizeColumns
                />
              </div>
              {recommendationPayload?.diagnostics.length ? (
                <details className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                  <summary className="cursor-pointer font-bold">Recommendation scan diagnostics</summary>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {recommendationPayload.diagnostics.slice(0, 10).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </details>
              ) : null}
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <DataTable table={rowsToTable('incidents', 'Incidents', payload.model.incidents)} storageKey="incidents" />
            <DataTable table={rowsToTable('vulnerabilities', 'Vulnerabilities', payload.model.vulnerabilities)} storageKey="vulnerabilities" />
            <DataTable table={tableById(payload, 'detections', 'Detections', payload.model.detections)} storageKey="detections" />
            <DataTable table={tableById(payload, 'data_sources', 'Data Sources', payload.model.dataSources)} storageKey="datasources" />
            <DataTable table={tableById(payload, 'users', 'Users / Identities', payload.model.users)} storageKey="users" />
            <DataTable table={tableById(payload, 'devices', 'Devices', payload.model.devices)} storageKey="devices" />
            <DataTable table={rowsToTable('playbooks', 'Playbooks', payload.model.playbooks)} storageKey="playbooks" />
            <DataTable table={rowsToTable('controls', 'Controls', payload.model.controls)} storageKey="controls" />
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {loading ? 'Building SOC Digital Twin…' : 'No Digital Twin data loaded yet.'}
        </div>
      )}
    </div>
  );
}
